import React from 'react';
import './Loading.css';

export type LoadingType = 
  | 'spinner' 
  | 'dots' 
  | 'pulse' 
  | 'bars' 
  | 'skeleton' 
  | 'skeleton-text' 
  | 'skeleton-card' 
  | 'skeleton-list';

export type LoadingSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface LoadingProps {
  type?: LoadingType;
  size?: LoadingSize;
  message?: string;
  overlay?: boolean;
  fullScreen?: boolean;
  color?: string;
  className?: string;
  lines?: number; // For skeleton-text
  items?: number; // For skeleton-list
  showMessage?: boolean;
  centered?: boolean;
  inline?: boolean;
}

const Loading: React.FC<LoadingProps> = ({
  type = 'spinner',
  size = 'medium',
  message,
  overlay = false,
  fullScreen = false,
  color,
  className = '',
  lines = 3,
  items = 3,
  showMessage = true,
  centered = true,
  inline = false,
}) => {
  // Get size classes
  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'loading--small';
      case 'medium': return 'loading--medium';
      case 'large': return 'loading--large';
      case 'xlarge': return 'loading--xlarge';
      default: return 'loading--medium';
    }
  };

  // Render spinner loading
  const renderSpinner = () => (
    <div className="loading__spinner" style={{ color }}>
      <svg
        className="loading__spinner-svg"
        viewBox="0 0 50 50"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          className="loading__spinner-circle"
          cx="25"
          cy="25"
          r="20"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );

  // Render dots loading
  const renderDots = () => (
    <div className="loading__dots" style={{ color }}>
      <div className="loading__dot"></div>
      <div className="loading__dot"></div>
      <div className="loading__dot"></div>
    </div>
  );

  // Render pulse loading
  const renderPulse = () => (
    <div className="loading__pulse" style={{ backgroundColor: color }}>
      <div className="loading__pulse-circle loading__pulse-circle--1"></div>
      <div className="loading__pulse-circle loading__pulse-circle--2"></div>
      <div className="loading__pulse-circle loading__pulse-circle--3"></div>
    </div>
  );

  // Render bars loading
  const renderBars = () => (
    <div className="loading__bars" style={{ color }}>
      <div className="loading__bar loading__bar--1"></div>
      <div className="loading__bar loading__bar--2"></div>
      <div className="loading__bar loading__bar--3"></div>
      <div className="loading__bar loading__bar--4"></div>
      <div className="loading__bar loading__bar--5"></div>
    </div>
  );

  // Render skeleton loading
  const renderSkeleton = () => (
    <div className="loading__skeleton">
      <div className="loading__skeleton-avatar"></div>
      <div className="loading__skeleton-content">
        <div className="loading__skeleton-line loading__skeleton-line--title"></div>
        <div className="loading__skeleton-line loading__skeleton-line--subtitle"></div>
        <div className="loading__skeleton-line loading__skeleton-line--text"></div>
      </div>
    </div>
  );

  // Render skeleton text loading
  const renderSkeletonText = () => (
    <div className="loading__skeleton-text">
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className={`loading__skeleton-line ${
            index === lines - 1 ? 'loading__skeleton-line--last' : ''
          }`}
        ></div>
      ))}
    </div>
  );

  // Render skeleton card loading
  const renderSkeletonCard = () => (
    <div className="loading__skeleton-card">
      <div className="loading__skeleton-card-image"></div>
      <div className="loading__skeleton-card-content">
        <div className="loading__skeleton-line loading__skeleton-line--title"></div>
        <div className="loading__skeleton-line loading__skeleton-line--subtitle"></div>
        <div className="loading__skeleton-line loading__skeleton-line--text"></div>
        <div className="loading__skeleton-line loading__skeleton-line--text loading__skeleton-line--short"></div>
      </div>
      <div className="loading__skeleton-card-footer">
        <div className="loading__skeleton-button"></div>
        <div className="loading__skeleton-button loading__skeleton-button--secondary"></div>
      </div>
    </div>
  );

  // Render skeleton list loading
  const renderSkeletonList = () => (
    <div className="loading__skeleton-list">
      {Array.from({ length: items }, (_, index) => (
        <div key={index} className="loading__skeleton-list-item">
          <div className="loading__skeleton-list-avatar"></div>
          <div className="loading__skeleton-list-content">
            <div className="loading__skeleton-line loading__skeleton-line--title"></div>
            <div className="loading__skeleton-line loading__skeleton-line--subtitle"></div>
          </div>
          <div className="loading__skeleton-list-action">
            <div className="loading__skeleton-button loading__skeleton-button--small"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Get loading content based on type
  const getLoadingContent = () => {
    switch (type) {
      case 'spinner': return renderSpinner();
      case 'dots': return renderDots();
      case 'pulse': return renderPulse();
      case 'bars': return renderBars();
      case 'skeleton': return renderSkeleton();
      case 'skeleton-text': return renderSkeletonText();
      case 'skeleton-card': return renderSkeletonCard();
      case 'skeleton-list': return renderSkeletonList();
      default: return renderSpinner();
    }
  };

  // Check if type is skeleton-based
  const isSkeletonType = type.startsWith('skeleton');

  // Build class names
  const loadingClasses = [
    'loading',
    getSizeClass(),
    `loading--${type}`,
    centered && !inline ? 'loading--centered' : '',
    inline ? 'loading--inline' : '',
    overlay ? 'loading--overlay' : '',
    fullScreen ? 'loading--fullscreen' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={loadingClasses}
      role="status"
      aria-live="polite"
      aria-label={message || 'Loading content'}
    >
      {/* Loading Animation */}
      <div className="loading__content">
        {getLoadingContent()}
        
        {/* Loading Message */}
        {message && showMessage && !isSkeletonType && (
          <div className="loading__message">
            {message}
          </div>
        )}
      </div>

      {/* Screen Reader Text */}
      <span className="loading__sr-only">
        {message || 'Loading, please wait...'}
      </span>
    </div>
  );
};

// Predefined loading configurations for common use cases
export const LoadingSpinner: React.FC<Omit<LoadingProps, 'type'>> = (props) => (
  <Loading {...props} type="spinner" />
);

export const LoadingDots: React.FC<Omit<LoadingProps, 'type'>> = (props) => (
  <Loading {...props} type="dots" />
);

export const LoadingPulse: React.FC<Omit<LoadingProps, 'type'>> = (props) => (
  <Loading {...props} type="pulse" />
);

export const LoadingBars: React.FC<Omit<LoadingProps, 'type'>> = (props) => (
  <Loading {...props} type="bars" />
);

export const LoadingSkeleton: React.FC<Omit<LoadingProps, 'type'>> = (props) => (
  <Loading {...props} type="skeleton" />
);

export const LoadingSkeletonText: React.FC<Omit<LoadingProps, 'type'>> = (props) => (
  <Loading {...props} type="skeleton-text" />
);

export const LoadingSkeletonCard: React.FC<Omit<LoadingProps, 'type'>> = (props) => (
  <Loading {...props} type="skeleton-card" />
);

export const LoadingSkeletonList: React.FC<Omit<LoadingProps, 'type'>> = (props) => (
  <Loading {...props} type="skeleton-list" />
);

// Full-screen overlay loading
export const LoadingOverlay: React.FC<Omit<LoadingProps, 'overlay' | 'fullScreen'>> = (props) => (
  <Loading {...props} overlay={true} fullScreen={true} />
);

// Inline loading for buttons and small spaces
export const LoadingInline: React.FC<Omit<LoadingProps, 'inline' | 'centered'>> = (props) => (
  <Loading {...props} inline={true} centered={false} size="small" />
);

export default Loading;
